// Naming by pattern (#471 for VLAN tags, #683 for a floor's rooms): one
// grammar in the product for "this name, with a number in it", and one
// reader for "these numbers". A pattern is a one-time expansion into rows
// (ADR-0064 decision 7) — the rows are what is sent and stored, and nothing
// here is a rule the server would keep. Pure, so a test can hold the list a
// form previews against the set it sends.

/** Expands a naming pattern for one number: `{id}` is the number, `{n}`
 * its position in the selection from 1, `:04` pads to four digits. */
export function expand(pattern: string, id: number, n: number): string {
  return pattern.replace(/\{(id|n)(?::(\d+))?\}/g, (_, k: string, pad?: string) => {
    const s = String(k === "id" ? id : n);
    return pad ? s.padStart(Number(pad), "0") : s;
  });
}

/** Whether a pattern varies with the number at all. A pattern with neither
 * placeholder expands to one name repeated, which over a list of numbers is
 * a set that cannot land (a name is unique at a site). */
export const hasPlaceholder = (pattern: string): boolean => /\{(id|n)(?::\d+)?\}/.test(pattern);

/** What a number list says — `200-212,214-260` — read into the numbers it
 * names, ascending, each once. A range is inclusive and may be written
 * either way round; separators are commas, spaces tolerated; an en dash
 * reads as a hyphen. `max` bounds the count: past it the answer is a
 * refusal, not a truncation, because a form that silently dropped the tail
 * would preview a set it does not send. */
export type NumberList = { numbers: number[]; error?: undefined } | { numbers?: undefined; error: string };

export function parseNumberList(text: string, max: number): NumberList {
  const t = text.trim();
  if (t === "") return { error: "Enter the numbers, for example 200-212, 214-260" };
  const seen = new Set<number>();
  for (const part of t.split(",")) {
    const m = part.trim().match(/^(\d{1,6})(?:\s*[-–]\s*(\d{1,6}))?$/);
    if (!m) return { error: `"${part.trim()}" is not a number or a range` };
    const a = Number(m[1]);
    const b = m[2] === undefined ? a : Number(m[2]);
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    if (hi - lo + 1 > max) return { error: `${lo}-${hi} alone names ${hi - lo + 1} — at most ${max} in one batch` };
    for (let i = lo; i <= hi; i++) {
      seen.add(i);
      if (seen.size > max) return { error: `That names more than ${max}; at most ${max} in one batch` };
    }
  }
  return { numbers: [...seen].sort((x, y) => x - y) };
}

/** The names a pattern makes over a list: one per number, in order, `{n}`
 * counting from 1. Trimmed, because a name carries no surrounding
 * whitespace at the server and a pattern typed with a trailing space
 * should not fail on it. */
export function expandAll(pattern: string, numbers: number[]): string[] {
  return numbers.map((id, i) => expand(pattern, id, i + 1).trim());
}
